import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { ArrowRight, Check, Globe, Palette, Users, ShoppingCart, FileText, Code, Bot, Sparkles, Search } from 'lucide-react'
import Breadcrumb from '../components/Breadcrumb'
import { services, additionalServices } from '../data/services'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const schema = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  "name": "Services - Technifire Software",
  "description": "8 core digital services: Website Development, Branding & Design, CRM Systems, E-Commerce, Landing Pages, SaaS Platforms, AI Automation & Chatbots, SEO & Digital Growth.",
  "url": "https://technifire.io/services",
  "itemListElement": [
    { "@type": "Service", "position": 1, "name": "Website Development" },
    { "@type": "Service", "position": 2, "name": "Branding & Design" },
    { "@type": "Service", "position": 3, "name": "CRM Systems" },
    { "@type": "Service", "position": 4, "name": "E-Commerce" },
    { "@type": "Service", "position": 5, "name": "Landing Pages" },
    { "@type": "Service", "position": 6, "name": "SaaS Platforms" },
    { "@type": "Service", "position": 7, "name": "AI Automation & Chatbots" },
    { "@type": "Service", "position": 8, "name": "SEO & Digital Growth" }
  ]
}

const serviceIcons: Record<string, React.ElementType> = {
  'web-dev': Globe,
  'branding': Palette,
  'crm': Users,
  'ecommerce': ShoppingCart,
  'landing': FileText,
  'saas': Code,
  'ai': Bot,
  'seo': Search,
}

export default function Services() {
  const sectionRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const ctx = gsap.context(() => {
      const elements = sectionRef.current?.querySelectorAll('.service-animate')
      if (elements && elements.length > 0) {
        gsap.fromTo(elements, { y: 50, opacity: 0 }, {
          y: 0, opacity: 1, duration: 0.7, stagger: 0.1, ease: 'power3.out',
          scrollTrigger: { trigger: sectionRef.current, start: 'top bottom', toggleActions: 'play none none none', invalidateOnRefresh: true }
        })
      }
    }, sectionRef)
    return () => ctx.revert()
  }, [])

  return (
    <div className="min-h-screen bg-[#050505] pb-20" ref={sectionRef}>
      <Helmet>
        <title>Web, SaaS, AI & Growth Services | Technifire</title>
        <meta name="description" content="8 core digital services: Website Development, Branding & Design, CRM Systems, E-Commerce, Landing Pages, SaaS Platforms, AI Automation & Chatbots, SEO & Digital Growth." />
        <link rel="canonical" href="https://technifire.io/services" />
        <meta property="og:title" content="Web, SaaS, AI & Growth Services | Technifire" />
        <meta property="og:description" content="From custom websites to AI-powered automation — we deliver end-to-end digital solutions that drive measurable business growth." />
        <meta property="og:url" content="https://technifire.io/services" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>
      <Breadcrumb current="SERVICES" />

      {/* Hero */}
      <div className="px-6 lg:px-10 pt-8 pb-16">
        <div className="service-animate mb-8">
          <span className="text-mono text-[#9A9A9A] mb-3 block">OUR SERVICES</span>
          <h1 className="text-h2 uppercase text-white mb-6 max-w-4xl">
            WEB, SOFTWARE, AI &<br />DIGITAL GROWTH SERVICES
          </h1>
          <p className="text-lg text-[#9A9A9A] max-w-2xl mb-8">
            From custom websites to AI-powered automation — we deliver end-to-end digital solutions
            that drive measurable business growth. Every service is designed to solve real problems
            and deliver real ROI.
          </p>
          <Link to="/contacts" className="btn-primary ">
            <Sparkles className="w-4 h-4" />
            BOOK A STRATEGY CALL
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Why choose us */}
        <div className="service-animate grid grid-cols-1 md:grid-cols-3 gap-6 mb-16 py-10 border-y border-white/10">
          {[
            { title: 'End-to-End Delivery', desc: 'From strategy to design to development to launch — one team, one accountability.' },
            { title: 'Results-Driven', desc: 'We measure success by your metrics: conversions, revenue, engagement, and growth.' },
            { title: 'Scalable Architecture', desc: 'Every product is built to grow with your business — no rebuilds needed.' },
          ].map((item) => (
            <div key={item.title}>
              <h3 className="text-white font-medium mb-2">{item.title}</h3>
              <p className="text-sm text-[#9A9A9A]">{item.desc}</p>
            </div>
          ))}
        </div>

        {/* Services List */}
        <div className="space-y-0">
          {services.map((service) => {
            const Icon = serviceIcons[service.id] || Globe
            return (
              <div key={service.id} className="service-animate border-t border-white/10 py-10 group">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10">
                  {/* Left: Number + Icon */}
                  <div className="lg:col-span-1 flex items-start gap-3">
                    <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center shrink-0 group-hover:bg-white/10 transition-colors">
                      <Icon className="w-5 h-5 text-[#9A9A9A] group-hover:text-white transition-colors" />
                    </div>
                  </div>

                  {/* Middle: Content */}
                  <div className="lg:col-span-7">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-mono text-[#9A9A9A]">{service.number}</span>
                      <h3 className="text-2xl lg:text-3xl text-white font-light">{service.title}</h3>
                    </div>
                    <p className="text-[#9A9A9A] mb-6 leading-relaxed">{service.description}</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-6">
                      {service.features.map((feature) => (
                        <div key={feature} className="flex items-center gap-2">
                          <Check className="w-3.5 h-3.5 text-[#4ADE80] shrink-0" />
                          <span className="text-sm text-white/70">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Right: Tech + CTA */}
                  <div className="lg:col-span-4 flex flex-col justify-between">
                    <div className="flex flex-wrap gap-2 mb-4">
                      {service.technologies.map((tech) => (
                        <span key={tech} className="px-3 py-1 bg-[#B7000E] rounded-full text-xs text-white">{tech}</span>
                      ))}
                    </div>
                    <Link to={`/services/${service.id}`} className="text-sm text-white hover:text-[#9A9A9A] transition-colors flex items-center gap-2">
                      Explore {service.title} <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Additional services */}
        <div className="mt-12 service-animate py-10 border-t border-white/10">
          <h3 className="text-mono text-[#9A9A9A] mb-4">ALSO AVAILABLE</h3>
          <div className="flex flex-wrap gap-3">
            {additionalServices.map((s) => (
              <span key={s} className="btn-outline hover:bg-[#B7000E] text-sm">{s}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
